import { useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import CloudDownloadOutlineIcon from 'mdi-react/CloudDownloadOutlineIcon'
import { useDispatch, useSelector } from 'react-redux'
import { compose } from 'redux'

import documentResources from '@/api/document'
import queries from '@/api/queries'
import Button from '@/components/form/Button'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'
import Error from '@/components/util/feedback/Error'
import LoaderCircle from '@/components/util/LoaderCircle'
import { getCustomerName, getCustomerOrgNumber } from '@/ducks/customerReducer'
import {
  getCreditCheckDocumentId,
  getHasCreditCheckError,
  getIsCreditCheckGranted,
  getIsCreditCheckPerformed,
  getIsLeasing,
  getOrderTotalCost,
  postCreditCheck,
} from '@/ducks/shopOrderReducer'
import withCustomersLoaded from '@/hocs/withCustomersLoaded'
import withQuoteCustomerLoaded from '@/hocs/withQuoteCustomerLoaded'
import { useIsBechtleDemoUser } from '@/hooks/user'

import CreditCheckResult from '../credit-check/CreditCheckResult'

import './step-credit-check.scss'

const StepCreditCheck = () => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)

  const isCreditCheckPerformed = useSelector(getIsCreditCheckPerformed)
  const isCreditCheckGranted = useSelector(getIsCreditCheckGranted)
  const hasCreditCheckError = useSelector(getHasCreditCheckError)

  const performCreditCheck = async () => {
    setIsLoading(true)
    // @ts-ignore
    await dispatch(postCreditCheck())
    setIsLoading(false)
  }

  return (
    <div className="shop-step__credit-check">
      {hasCreditCheckError && (
        <Error>
          <Translation id="Something failed during credit check. Are all input correctly provided?" />
        </Error>
      )}

      <div className="shop-step__credit-check__content">
        {isCreditCheckPerformed ? (
          <CreditCheckResult />
        ) : (
          <CreditCheckButton performCreditCheck={performCreditCheck} isLoading={isLoading} />
        )}
      </div>

      <DetailsTable />

      {isCreditCheckGranted && <DownloadCreditCheckPdf />}
    </div>
  )
}

// @ts-ignore
const CreditCheckButton = ({ performCreditCheck, isLoading }) => {
  const { buying_sum } = useSelector(getOrderTotalCost)
  const isBechtleDemoUser = useIsBechtleDemoUser()

  return isLoading ? (
    <>
      <LoaderCircle />
      {isBechtleDemoUser && (
        <h2 className="demo-text">
          <Translation id="Searching for optimal funder..." />
        </h2>
      )}
    </>
  ) : (
    <Button onClick={performCreditCheck} disabled={buying_sum <= 0}>
      <Translation id="Perform credit check" />
    </Button>
  )
}

const DetailsTable = () => {
  const isLeasing = useSelector(getIsLeasing)
  const isCreditCheckGranted = useSelector(getIsCreditCheckGranted)
  const isBechtleDemoUser = useIsBechtleDemoUser()

  const name = useSelector(getCustomerName)
  const orgNumber = useSelector(getCustomerOrgNumber)

  const { buying_sum, leasing_sum, leasing_duration_months } = useSelector(getOrderTotalCost)
  const details = {
    'Organization Name': name,
    'Organization Number': orgNumber,
    'Deal size': <FormattedNumberWithCurrency amount={isLeasing ? leasing_sum : buying_sum} />,
    'Maximum lease terms': `${leasing_duration_months} months`,
  }

  if (isCreditCheckGranted && isBechtleDemoUser) {
    // @ts-ignore
    details['Funder'] = 'HPE'
  }

  return (
    <table className="details-table">
      <tbody>
        {Object.keys(details).map(key => (
          <tr key={key}>
            <td>{key}</td>
            {/* @ts-ignore */}
            <td>{details[key]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const DownloadCreditCheckPdf = () => {
  const creditCheckDocumentId = useSelector(getCreditCheckDocumentId)

  // todo: TEST when this feature is in use again
  const { refetch: authorizeFileDownload, isInitialLoading: isDownloading } = useQuery(
    queries.documentDownloadAuthorization(creditCheckDocumentId),
    () => documentResources.authorizeFileDownload(creditCheckDocumentId),
    {
      enabled: false,
    }
  )

  if (!creditCheckDocumentId) return null

  return (
    <div className="download-credti-check-pdf">
      <Button
        Icon={CloudDownloadOutlineIcon}
        isLoading={isDownloading}
        onClick={authorizeFileDownload}
      >
        Download PDF
      </Button>
    </div>
  )
}

// @ts-ignore
export default compose(withCustomersLoaded, withQuoteCustomerLoaded)(StepCreditCheck)
